import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getExpenses, deleteExpense } from '../../api/finance';

const fmt = (n, cur = 'TRY') => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: cur }).format(n ?? 0);

const STATUS_LABELS = {
  pending:  { label: 'Beklemede', color: 'bg-yellow-100 text-yellow-700' },
  approved: { label: 'Onaylandi', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Reddedildi', color: 'bg-red-100 text-red-700' },
  deferred: { label: 'Ertelendi', color: 'bg-gray-100 text-gray-600' },
};

const CAT_LABELS = {
  ads: 'Reklam', freelancer: 'Freelancer', salary: 'Maas',
  software: 'Yazilim', office: 'Ofis', tax: 'Vergi', other: 'Diger',
};

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [catFilter, setCatFilter] = useState('');

  const load = () => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (catFilter) params.category = catFilter;
    getExpenses(params)
      .then((r) => setExpenses(r.data))
      .catch(() => alert('Giderler yuklenemedi'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter, catFilter]);

  const handleDelete = async (id) => {
    if (!window.confirm('Silmek istediginizden emin misiniz?')) return;
    try { await deleteExpense(id); load(); } catch { alert('Silme basarisiz'); }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Giderler</h1>
        <Link to="/finance/expenses/new" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition">+ Yeni Gider</Link>
      </div>

      <div className="flex gap-2 flex-wrap">
        {[['', 'Tum Durumlar'], ['pending', 'Beklemede'], ['approved', 'Onaylandi'], ['rejected', 'Reddedildi'], ['deferred', 'Ertelendi']].map(([val, label]) => (
          <button key={val} onClick={() => setStatusFilter(val)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${statusFilter === val ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-300'}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="flex gap-2 flex-wrap">
        {[['', 'Tum Kategoriler'], ['ads', 'Reklam'], ['freelancer', 'Freelancer'], ['salary', 'Maas'], ['software', 'Yazilim'], ['office', 'Ofis'], ['tax', 'Vergi'], ['other', 'Diger']].map(([val, label]) => (
          <button key={val} onClick={() => setCatFilter(val)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${catFilter === val ? 'bg-purple-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-purple-300'}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : expenses.length === 0 ? (
          <div className="text-center py-16 text-gray-400"><p className="text-4xl mb-2">💸</p><p>Gider bulunamadi</p></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left">Baslik</th>
                <th className="px-4 py-3 text-left">Kategori</th>
                <th className="px-4 py-3 text-left">Tedarikci</th>
                <th className="px-4 py-3 text-right">Tutar</th>
                <th className="px-4 py-3 text-center">Durum</th>
                <th className="px-4 py-3 text-right">Tarih</th>
                <th className="px-4 py-3 text-center">Islem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {expenses.map((exp) => {
                const st = STATUS_LABELS[exp.status] || STATUS_LABELS.pending;
                return (
                  <tr key={exp.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 font-medium text-gray-800">{exp.title}</td>
                    <td className="px-4 py-3 text-gray-500">{CAT_LABELS[exp.category] || exp.category}</td>
                    <td className="px-4 py-3 text-gray-500">{exp.vendor_name || '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmt(exp.amount, exp.currency)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.color}`}>{st.label}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500">{exp.expense_date ? new Date(exp.expense_date).toLocaleDateString('tr-TR') : '—'}</td>
                    <td className="px-4 py-3 text-center">
                      {exp.status === 'pending' && (
                        <button onClick={() => handleDelete(exp.id)} className="text-xs bg-red-50 text-red-500 px-2 py-1 rounded hover:bg-red-100 transition">Sil</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
